// Studio OS — Recall data layer
//
// Recall owns no sessions of its own. A capture belongs to an input sheet,
// which belongs to a booking in the calendar. Two kinds of data meet here:
//
//   persisted — what the engineer types or shoots: notes, captions, dial
//               settings, photos. Stored per sheet id under "recall_captures"
//               (photos live in "recall_photos", keyed by slot id).
//   derived   — what the studio already knows: which outboard is patched and
//               which channels carry mics (from the sheet's line list), and
//               what gear is checked out to the session (from the inventory).
//
// Nothing is hard-coded per session. rcBuildCapture(sheet, booking) merges the
// two at render time, so a capture stays correct as the line list changes.

// ── Photo store ───────────────────────────────────────────────────────
// Uploaded images are downscaled to JPEG data-URLs and kept in one map keyed
// by slot id. Cached in memory after first read so scattered slots don't each
// re-parse localStorage.
const RC_PHOTO_KEY = "recall_photos";
let _rcPhotos = null;

function rcPhotos() {
  if (_rcPhotos === null) _rcPhotos = load(STORE_ORG, RC_PHOTO_KEY, {}) || {};
  return _rcPhotos;
}
function rcGetPhoto(slotId) { return (slotId && rcPhotos()[slotId]) || null; }
function rcSetPhoto(slotId, dataUrl) {
  const map = rcPhotos();
  if (dataUrl) map[slotId] = dataUrl; else delete map[slotId];
  save(STORE_ORG, RC_PHOTO_KEY, map);
}

// ── Capture store ─────────────────────────────────────────────────────
const RC_CAPTURE_KEY = "recall_captures";
let _rcCaptures = null;

function rcCaptureStore() {
  if (_rcCaptures === null) _rcCaptures = load(STORE_ORG, RC_CAPTURE_KEY, {}) || {};
  return _rcCaptures;
}
function rcGetSaved(sheetId) { return rcCaptureStore()[sheetId] || {}; }

// Wipes a sheet's whole capture — used when the sheet itself is deleted, not
// for clearing an individual field. Every slot id for a sheet is prefixed
// "rc:{sheetId}:" (see rcSlot), so that's also the photo-key prefix to sweep.
function rcDeleteCapture(sheetId) {
  const store = rcCaptureStore();
  if (store[sheetId]) {
    delete store[sheetId];
    save(STORE_ORG, RC_CAPTURE_KEY, store);
  }
  const photos = rcPhotos();
  const prefix = rcSlot(sheetId, "");
  let changed = false;
  for (const k of Object.keys(photos)) {
    if (k.startsWith(prefix)) { delete photos[k]; changed = true; }
  }
  if (changed) save(STORE_ORG, RC_PHOTO_KEY, photos);
}

// Mutate one sheet's saved capture and persist. Stamps capturedAt so the
// workspace can show when it was last touched.
function rcPatchCapture(sheetId, mutate) {
  const store = rcCaptureStore();
  const rec = store[sheetId] || (store[sheetId] = {});
  mutate(rec);
  rec.capturedAt = new Date().toISOString().slice(0, 19);
  save(STORE_ORG, RC_CAPTURE_KEY, store);
  return rec;
}
// Per-gear fields (notes, player) live in one map keyed by inventory id, so a
// unit that appears as both a patched insert and checked-out gear keeps one
// set of notes.
function rcSetUnitField(sheetId, unitId, field, value) {
  rcPatchCapture(sheetId, rec => {
    rec.units = rec.units || {};
    rec.units[unitId] = { ...(rec.units[unitId] || {}), [field]: value };
  });
}
function rcSetConsoleField(sheetId, field, value) {
  rcPatchCapture(sheetId, rec => {
    rec.console = { ...(rec.console || {}), [field]: value };
    if (field === "importSource") rec.console.imported = !!String(value || "").trim();
  });
}
function rcSetNotes(sheetId, notes) {
  rcPatchCapture(sheetId, rec => { rec.notes = notes; });
}
function rcAddManualUnit(sheetId) {
  const id = "RC-OUT-" + Date.now().toString(36).toUpperCase();
  rcPatchCapture(sheetId, rec => {
    rec.manualUnits = rec.manualUnits || [];
    rec.manualUnits.push({ id, manufacturer: "", model: "", source: "" });
  });
  return id;
}
function rcAddBacklineEntry(sheetId) {
  const id = "RC-BL-" + Date.now().toString(36).toUpperCase();
  rcPatchCapture(sheetId, rec => {
    rec.backline = rec.backline || [];
    rec.backline.push({ id, name: "", kind: "", player: "" });
  });
  return id;
}
// Manual entries can be removed; derived ones can't — they come from the line
// list or the checkout, and that's where they have to be changed.
function rcRemoveManualEntry(sheetId, unitId) {
  rcPatchCapture(sheetId, rec => {
    rec.manualUnits = (rec.manualUnits || []).filter(u => u.id !== unitId);
    rec.backline    = (rec.backline || []).filter(u => u.id !== unitId);
    if (rec.units) delete rec.units[unitId];
  });
  rcSetPhoto(rcSlot(sheetId, "unit", unitId), null);
  rcSetPhoto(rcSlot(sheetId, "bl", unitId), null);
}
function rcSetManualUnitField(sheetId, unitId, field, value) {
  rcPatchCapture(sheetId, rec => {
    for (const list of [rec.manualUnits || [], rec.backline || []]) {
      const e = list.find(u => u.id === unitId);
      if (e) e[field] = value;
    }
  });
}
function rcAddRoomPhoto(sheetId) {
  rcPatchCapture(sheetId, rec => {
    rec.room = rec.room || {};
    rec.room.photos = rec.room.photos || [];
    const base = Math.max(RC_ROOM_SLOTS, rec.room.photos.length);
    while (rec.room.photos.length <= base) rec.room.photos.push({ caption: "" });
  });
}
function rcAddMic(sheetId) {
  const id = "RC-MIC-" + Date.now().toString(36).toUpperCase();
  rcPatchCapture(sheetId, rec => {
    rec.micList = rec.micList || [];
    rec.micList.push({ id, source: "", mic: "", caption: "" });
  });
  return id;
}
function rcSetMicField(sheetId, micId, field, value) {
  rcPatchCapture(sheetId, rec => {
    const m = (rec.micList || []).find(x => x.id === micId);
    if (m) m[field] = value;
  });
}
function rcRemoveMic(sheetId, micId) {
  rcPatchCapture(sheetId, rec => { rec.micList = (rec.micList || []).filter(x => x.id !== micId); });
  rcSetPhoto(rcSlot(sheetId, "mic", micId), null);
}
function rcSetRoomCaption(sheetId, idx, caption) {
  rcPatchCapture(sheetId, rec => {
    rec.room = rec.room || {};
    rec.room.photos = rec.room.photos || [];
    while (rec.room.photos.length <= idx) rec.room.photos.push({ caption: "" });
    rec.room.photos[idx] = { ...(rec.room.photos[idx] || {}), caption };
  });
}

// ── Slot ids ──────────────────────────────────────────────────────────
// Deterministic, so a photo stays attached to its subject across reloads and
// across edits to the line list.
function rcSlot(sheetId, kind, key) {
  return key === undefined ? `rc:${sheetId}:${kind}` : `rc:${sheetId}:${kind}:${key}`;
}

// ── Inventory lookups ─────────────────────────────────────────────────
// Sheets reference gear by id. Ids that aren't in the inventory (older sheets,
// borrowed gear) still render — they just show the bare id.
function rcInvItem(id) {
  if (!id || id === "—") return null;
  const inv = (typeof INVENTORY !== "undefined") ? INVENTORY : [];
  return inv.find(i => i.id === id) || null;
}
function rcGearLabel(id) {
  const it = rcInvItem(id);
  if (!it) return { manufacturer: "", model: id || "" };
  return { manufacturer: it.manufacturer || "", model: it.model || id };
}
function rcGearName(id) {
  const { manufacturer, model } = rcGearLabel(id);
  return [manufacturer, model].filter(Boolean).join(" ").trim() || id;
}

// Sheets written by the Input Sheets module use `channels`; the seeded
// tracking sheets use `rows`. Same shape either way.
function rcSheetChannels(sheet) {
  return (sheet && (sheet.channels || sheet.rows)) || [];
}

// Categories that count as backline rather than outboard when gear is checked
// out to a session.
const RC_BACKLINE_CATEGORIES = new Set([
  "Guitar", "Bass", "Drums", "Keys", "Amplifier", "Percussion", "Instrument", "Drum Machine", "Pedal",
]);

const RC_ROOM_SLOTS = 3;

// ── Derivation ────────────────────────────────────────────────────────

function rcDeriveConsole(sheet, saved) {
  const c = saved.console || {};
  return {
    imported: !!c.imported,
    importSource: c.importSource || "",
    notes: c.notes || "",
    slotId: rcSlot(sheet.id, "console"),
  };
}

// Outboard = Outboard-category gear checked out to this booking, plus units
// added by hand. Recall doesn't read the input sheet — the engineer decides
// what's worth capturing.
function rcDeriveOutboard(sheet, booking, saved) {
  const units = new Map();
  const add = (id, role, source) => {
    if (!id || id === "—") return;
    let u = units.get(id);
    if (!u) { u = { id, role, sources: [] }; units.set(id, u); }
    if (source) u.sources.push(source);
  };

  if (booking && typeof INVENTORY !== "undefined") {
    for (const i of INVENTORY) {
      if (i.sessionId !== booking.id) continue;
      if (i.category !== "Outboard") continue;
      if (!units.has(i.id)) units.set(i.id, { id: i.id, role: "Checked out", sources: [] });
    }
  }

  const savedUnits = saved.units || {};
  const out = Array.from(units.values()).map(u => {
    const { manufacturer, model } = rcGearLabel(u.id);
    // A bus preamp can serve a dozen channels; the card header only has room
    // for a few, so summarise and keep the full list for the tooltip.
    const sources = Array.from(new Set(u.sources));
    const shown = sources.slice(0, 3).join(", ");
    return {
      id: u.id,
      role: u.role,
      manufacturer,
      model,
      source: sources.length
        ? (sources.length > 3 ? `${shown} +${sources.length - 3}` : shown)
        : u.role,
      sourcesFull: sources.join(", "),
      slotId: rcSlot(sheet.id, "unit", u.id),
      notes: savedUnits[u.id]?.notes || "",
      manual: false,
    };
  });

  for (const m of (saved.manualUnits || [])) {
    if (units.has(m.id)) continue;
    out.push({
      id: m.id,
      role: "Added",
      // Hand-added units render as editable fields, so leave blanks blank —
      // pre-filling with the generated id would just be noise to delete.
      manufacturer: m.manufacturer || "",
      model: m.model || "",
      source: m.source || "Added by hand",
      slotId: rcSlot(sheet.id, "unit", m.id),
      notes: savedUnits[m.id]?.notes || "",
      manual: true,
    });
  }
  return out;
}

// Backline = instruments and amps checked out to this booking, plus anything
// added by hand (the band's own gear, which isn't in the studio inventory).
function rcDeriveBackline(sheet, booking, saved) {
  const savedUnits = saved.units || {};
  const out = [];
  const seen = new Set();

  if (booking && typeof INVENTORY !== "undefined") {
    for (const i of INVENTORY) {
      if (i.sessionId !== booking.id) continue;
      if (!RC_BACKLINE_CATEGORIES.has(i.category)) continue;
      seen.add(i.id);
      out.push({
        id: i.id,
        name: rcGearName(i.id),
        kind: i.subcat && i.subcat !== "—" ? i.subcat : i.category,
        player: savedUnits[i.id]?.player || "",
        slotId: rcSlot(sheet.id, "bl", i.id),
        settings: savedUnits[i.id]?.notes || "",
        manual: false,
      });
    }
  }

  for (const e of (saved.backline || [])) {
    if (seen.has(e.id)) continue;
    out.push({
      id: e.id,
      name: e.name || "",
      kind: e.kind || "",
      player: e.player || "",
      slotId: rcSlot(sheet.id, "bl", e.id),
      settings: savedUnits[e.id]?.notes || "",
      manual: true,
    });
  }
  return out;
}

// Mic placements are added by hand — one card per mic worth recalling.
function rcDeriveMics(sheet, saved) {
  return (saved.micList || []).map(m => ({
    id: m.id,
    source: m.source || "",
    mic: m.mic || "",
    slotId: rcSlot(sheet.id, "mic", m.id),
    caption: m.caption || "",
  }));
}

function rcDeriveRoom(sheet, saved) {
  const savedPhotos = (saved.room && saved.room.photos) || [];
  const count = Math.max(RC_ROOM_SLOTS, savedPhotos.length);
  const photos = [];
  for (let i = 0; i < count; i++) {
    photos.push({ slotId: rcSlot(sheet.id, "room", i), caption: savedPhotos[i]?.caption || "" });
  }
  return { photos, readings: (saved.room && saved.room.readings) || {} };
}

// The one entry point: everything Recall renders for a sheet.
function rcBuildCapture(sheet, booking) {
  const saved = rcGetSaved(sheet.id);
  return {
    sheetId: sheet.id,
    capturedAt: saved.capturedAt || null,
    capturedBy: saved.capturedBy || null,
    console:  rcDeriveConsole(sheet, saved),
    outboard: rcDeriveOutboard(sheet, booking, saved),
    backline: rcDeriveBackline(sheet, booking, saved),
    mics:     rcDeriveMics(sheet, saved),
    room:     rcDeriveRoom(sheet, saved),
    notes:    saved.notes || "",
  };
}

// ── Completion ────────────────────────────────────────────────────────
// "Done" is measured against what's actually stored — a photo in the slot or
// text in the field — never against a flag someone set in a fixture.
function rcSections(capture) {
  const shot = id => !!rcGetPhoto(id);
  const c = capture;
  const outDone  = c.outboard.filter(u => shot(u.slotId) || u.notes).length;
  const blDone   = c.backline.filter(u => shot(u.slotId) || u.settings).length;
  const micDone  = c.mics.filter(m => shot(m.slotId)).length;
  const roomDone = c.room.photos.filter(p => shot(p.slotId)).length;
  const consoleDone = !!(c.console.imported || shot(c.console.slotId) || c.console.notes);

  return [
    { id: "console", label: "Console state", glyph: "▤",
      done: consoleDone,
      meta: c.console.imported ? "imported" : (shot(c.console.slotId) ? "photo" : (c.console.notes ? "noted" : "empty")) },
    { id: "outboard", label: "Outboard", glyph: "▥",
      done: outDone > 0 && outDone === c.outboard.length, count: outDone, total: c.outboard.length },
    { id: "backline", label: "Backline", glyph: "♪",
      done: blDone > 0 && blDone === c.backline.length, count: blDone, total: c.backline.length },
    { id: "mics", label: "Mic placements", glyph: "◉",
      done: micDone > 0 && micDone === c.mics.length, count: micDone, total: c.mics.length },
    { id: "room", label: "Room state", glyph: "◐",
      done: roomDone > 0, count: roomDone, total: c.room.photos.length },
    { id: "notes", label: "Engineer's notes", glyph: "✎",
      done: !!c.notes, meta: c.notes ? "written" : "empty" },
  ];
}

// Percent complete, weighted by item rather than by section so a 20-mic sheet
// doesn't read as finished after one photo.
function rcProgress(capture) {
  const sections = rcSections(capture);
  let done = 0, total = 0;
  for (const s of sections) {
    if (s.total !== undefined) {
      if (s.total === 0) continue;   // nothing to capture — not counted either way
      done += s.count; total += s.total;
    } else {
      done += s.done ? 1 : 0; total += 1;
    }
  }
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

// Progress for a sheet without building the whole capture at the call site —
// used by the session picker.
function rcSheetProgress(sheet, booking) {
  return rcProgress(rcBuildCapture(sheet, booking));
}

// ── Seed content ──────────────────────────────────────────────────────
// Demo captures for the seeded Marlowe Hayes tracking sheets, in the same
// shape the app writes. store.jsx copies this into "recall_captures" once, on
// the migration that introduces the key — after that it's ordinary user data
// and nothing reads this map again.
const RECALL_SEED_CAPTURES = {
  "SHEET-MH-DAYBREAK": {
    capturedAt: "2026-04-22T19:30:00",
    capturedBy: "Rae Okafor",
    console: {
      imported: true,
      importSource: "Pro Tools session · 96k_24b_v14.ptx",
      notes: "Pushed lead vox fader +2 for the second chorus. Saved snapshot 'TK14-final'.",
    },
    units: {
      "OUT-001": { notes: "All-buttons-in for chorus 2 only. Input 5o'c, Output 3o'c, Attack 7, Release 5." },
      "OUT-002": { notes: "Ratio 8:1 · GR -3 average · faster attack than OUT-001" },
      "OUT-004": { notes: "30Hz boost AND atten on same band (Motown low). HF off." },
      "PRE-003": { notes: "Drum bus pres matched — gain staged at 38 across kit." },
      "BL-KIT":  { notes: "18/12/14 · Coated Ambassadors, tuned medium-low. Felt strip in kick, no front head port.", player: "Theo" },
      "BL-BASS": { notes: "Flats, tone rolled 70%. Through Avalon U5 DI — no amp today.", player: "Marlowe" },
      "BL-GTR":  { notes: "Neck pickup, tone 6. Into Deluxe Reverb — Vol 4, Treble 5, Bass 6, Reverb 3.", player: "Jaye" },
      "BL-PIANO":{ notes: "Lid on long stick. Tuned 04/22 morning.", player: "Marlowe" },
    },
    manualUnits: [
      { id: "OUT-004", manufacturer: "Pultec", model: "EQP-1A3", source: "Lead Vox bus" },
    ],
    backline: [
      { id: "BL-KIT",   name: "Gretsch USA Custom", kind: "Drum kit",       player: "Theo" },
      { id: "BL-BASS",  name: "Fender '73 P-Bass",  kind: "Bass + rig",     player: "Marlowe" },
      { id: "BL-GTR",   name: "Fender '62 Strat",   kind: "Elec. guitar",   player: "Jaye" },
      { id: "BL-PIANO", name: "Steinway B (7')",    kind: "Acoustic piano", player: "Marlowe" },
    ],
    micList: [
      { id: "RC-MIC-KICK",  source: "Kick In",  mic: "Shure Beta 91A", caption: "Inside, off batter, 2\" from beater" },
      { id: "RC-MIC-SNARE", source: "Snare Top", mic: "Shure SM57",    caption: "1\" off rim, 45°" },
      { id: "RC-MIC-OH",    source: "Overheads", mic: "Coles 4038 pair", caption: "XY pair, 4'2\" above snare" },
      { id: "RC-MIC-AG",    source: "Ac. Guitar", mic: "Neumann KM84",  caption: "12th fret, 8\" out, 30° angle" },
      { id: "RC-MIC-VOX",   source: "Lead Vox",  mic: "Neumann U47",   caption: "Pop filter 4\", Marlowe facing west wall" },
    ],
    room: {
      photos: [
        { caption: "Live Room — front of kit" },
        { caption: "Live Room — behind drummer" },
        { caption: "Vocal booth" },
      ],
      readings: {
        temperature: "68°F",
        humidity: "44%",
        hvac: "Quiet mode (NC-15)",
        lighting: "House lamps only · vocal booth: amber sconce",
      },
    },
    notes: "Daybreak Tk 14 — keeper. Marlowe nailed the bridge on this one. Pushed 1176 to all-buttons-in for the second chorus only (printed both versions). Pultec doing the Motown low trick on the vox bus. Plate verb panned hard for the gentle tail. Don't break this up — leave kit + mic positions for tomorrow's Heliotrope start.",
  },

  "SHEET-MH-CHECK": {
    capturedAt: "2026-04-22T10:15:00",
    capturedBy: "Rae Okafor",
    console: { imported: false, importSource: "", notes: "Unity gain on all strips before tracking." },
    units: {
      "BL-KIT": { notes: "Pre-session kit layout for line check.", player: "Theo" },
    },
    backline: [
      { id: "BL-KIT", name: "Gretsch USA Custom", kind: "Drum kit", player: "Theo" },
    ],
    room: {
      photos: [{ caption: "Pre-session room layout" }, { caption: "" }, { caption: "" }],
      readings: { temperature: "68°F", humidity: "44%", hvac: "Quiet", lighting: "House" },
    },
    notes: "",
  },
};

Object.assign(window, {
  RECALL_SEED_CAPTURES,
  rcGetPhoto, rcSetPhoto,
  rcGetSaved, rcPatchCapture, rcDeleteCapture, rcSetUnitField, rcSetConsoleField, rcSetNotes,
  rcAddMic, rcSetMicField, rcRemoveMic, rcSetRoomCaption, rcAddRoomPhoto,
  rcAddManualUnit, rcAddBacklineEntry, rcRemoveManualEntry, rcSetManualUnitField,
  rcSlot, rcInvItem, rcGearLabel, rcGearName, rcSheetChannels,
  rcBuildCapture, rcSections, rcProgress, rcSheetProgress,
});
